import React, { useState, useEffect } from 'react';
import '../styles/AgendaBuilder.css';

export interface AgendaItem {
    id: string;
    title: string;
    duration: number; // in minutes
    owner: string;
}

interface AgendaBuilderProps {
    initialAgenda: AgendaItem[];
    meetingStart: Date;
    meetingDuration: number; // in minutes
    onChange: (agenda: AgendaItem[]) => void;
}

const AgendaBuilder: React.FC<AgendaBuilderProps> = ({
    initialAgenda,
    meetingStart,
    meetingDuration,
    onChange
}) => {
    const [items, setItems] = useState<AgendaItem[]>(initialAgenda);

    useEffect(() => {
        setItems(initialAgenda);
    }, [initialAgenda]);

    const handleAddItem = () => {
        const newItem: AgendaItem = {
            id: crypto.randomUUID(),
            title: '',
            duration: 15,
            owner: ''
        };
        const newItems = [...items, newItem];
        setItems(newItems);
        onChange(newItems);
    };

    const handleUpdateItem = (id: string, field: keyof AgendaItem, value: any) => {
        const newItems = items.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        );
        setItems(newItems);
        onChange(newItems);
    };

    const handleDeleteItem = (id: string) => {
        const newItems = items.filter(item => item.id !== id);
        setItems(newItems);
        onChange(newItems);
    };

    const applyTemplate = (type: 'standup' | 'retro' | 'planning') => {
        let templateItems: AgendaItem[] = [];

        switch (type) {
            case 'standup':
                templateItems = [
                    { id: crypto.randomUUID(), title: 'Blockers & Updates', duration: 10, owner: 'All' },
                    { id: crypto.randomUUID(), title: 'Action Items', duration: 5, owner: 'Lead' }
                ];
                break;
            case 'retro':
                templateItems = [
                    { id: crypto.randomUUID(), title: 'What went well?', duration: 15, owner: 'All' },
                    { id: crypto.randomUUID(), title: 'What can be improved?', duration: 15, owner: 'All' },
                    { id: crypto.randomUUID(), title: 'Action Items', duration: 15, owner: 'Lead' }
                ];
                break;
            case 'planning':
                templateItems = [
                    { id: crypto.randomUUID(), title: 'Goal Review', duration: 10, owner: 'PM' },
                    { id: crypto.randomUUID(), title: 'Task Breakdown', duration: 40, owner: 'Team' },
                    { id: crypto.randomUUID(), title: 'Estimations', duration: 10, owner: 'Team' }
                ];
                break;
        }

        const newItems = [...items, ...templateItems];
        setItems(newItems);
        onChange(newItems);
    };

    const totalDuration = items.reduce((acc, item) => acc + (Number(item.duration) || 0), 0);
    const isOverTime = totalDuration > meetingDuration;

    const calculateStartTime = (index: number) => {
        let minutesToAdd = 0;
        for (let i = 0; i < index; i++) {
            minutesToAdd += Number(items[i].duration) || 0;
        }
        const startTime = new Date(meetingStart.getTime() + minutesToAdd * 60000);
        return startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="agenda-builder">
            <div className="agenda-header">
                <h3>Meeting Agenda</h3>
                <div className="agenda-stats">
                    <div className="stat-item">
                        <span>Total Time:</span>
                        <span className={`stat-value ${isOverTime ? 'over-time' : ''}`}>
                            {totalDuration}m / {meetingDuration}m
                        </span>
                    </div>
                    <div className="stat-item">
                        <span>Items:</span>
                        <span className="stat-value">{items.length}</span>
                    </div>
                </div>
            </div>

            <div className="agenda-list">
                {items.map((item, index) => (
                    <div key={item.id} className="agenda-item-card">
                        <div className="item-time-info">
                            <span className="item-start-time">{calculateStartTime(index)}</span>
                            <span className="item-duration">{item.duration}m</span>
                        </div>
                        <div className="item-content">
                            <div className="item-header">
                                <input
                                    type="text"
                                    className="item-title-input"
                                    placeholder="Agenda topic"
                                    value={item.title}
                                    onChange={(e) => handleUpdateItem(item.id, 'title', e.target.value)}
                                />
                                <button
                                    className="btn-delete-item"
                                    onClick={() => handleDeleteItem(item.id)}
                                    title="Remove item"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="item-details">
                                <input
                                    type="text"
                                    className="item-owner-input"
                                    placeholder="Owner"
                                    value={item.owner}
                                    onChange={(e) => handleUpdateItem(item.id, 'owner', e.target.value)}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Duration:</span>
                                    <input
                                        type="number"
                                        className="item-duration-input"
                                        value={item.duration}
                                        min="1"
                                        onChange={(e) => handleUpdateItem(item.id, 'duration', parseInt(e.target.value) || 0)}
                                    />
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>min</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <button className="add-item-btn" onClick={handleAddItem}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    Add Agenda Item
                </button>
            </div>

            <div className="templates-section">
                <div className="templates-title">Quick Templates</div>
                <div className="template-chips">
                    <button className="template-chip" onClick={() => applyTemplate('standup')}>Daily Standup</button>
                    <button className="template-chip" onClick={() => applyTemplate('retro')}>Retrospective</button>
                    <button className="template-chip" onClick={() => applyTemplate('planning')}>Sprint Planning</button>
                </div>
            </div>
        </div>
    );
};

export default AgendaBuilder;
